/**
 * Test Data catalog — workspace list view (#1258 Slice 2 +
 * post-merge corrections).
 *
 * The first surface an operator hits after the new nav entry
 * activates. Renders every TestDataSet in the workspace as a
 * polished catalog table: name + columns + row count + used-by
 * count + last-modified (with the operator's name resolved
 * server-side) + a clickable navigation into the matrix editor.
 *
 * Search box filters by Set name OR column name (case-
 * insensitive); the sort menu offers name / row count / used-by /
 * last modified. "+ New Set" opens a tiny name-only modal that
 * creates the Set and redirects straight to the matrix editor
 * with `$CONSUMED` already seeded.
 *
 * Vocabulary lock: "Used by" replaces every prior "Bound to"
 * surface (post-merge corrections). Code identifiers stay
 * (`DataBinding`, `bindingId`); user-visible strings use the
 * action-oriented "Used by" / "Tests using this Set" everywhere.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  ChevronDown,
  Trash2,
  Lock,
  Database,
  Link2,
} from 'lucide-react'
import {
  Button,
  Input,
  Badge,
  Card,
  EmptyState,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { testDataSetsAPI, type TestDataSetSummary } from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { useConfirm } from '@/hooks/useConfirm'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { useAppContext } from '@/hooks/useAppContext'

type SortKey = 'updated' | 'name' | 'rows' | 'usedBy'

export default function TestDataSetsCatalog() {
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const toast = useToast()
  const confirm = useConfirm()
  // Scope to the app + variant selected in the header — same as Tests,
  // Runs, Start, and Agent Memory (#1413 follow-up). The catalog used
  // to be workspace-wide with a manual App filter; operators expected
  // it to follow the header selection like every other variant-scoped
  // surface, so it now reads the global selection directly.
  const { selectedApp, selectedAppId, selectedVariant, selectedVariantId } = useAppContext()

  const [sets, setSets] = useState<TestDataSetSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('updated')
  const [newOpen, setNewOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  async function refresh() {
    // Guard: nothing to list until both app + variant are chosen. The
    // render below shows the "pick an app and variant" prompt in that
    // case; this keeps an unscoped (whole-workspace) request from ever
    // firing.
    if (!selectedAppId || !selectedVariantId) return
    try {
      const data = await testDataSetsAPI.list({ applicationId: selectedAppId, variantId: selectedVariantId })
      setSets(data.sets)
      // Clear a previous error on a successful refresh — without
      // this, a transient failure pinned the message forever.
      // Copilot review on #1266.
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // Re-list whenever the selected app or variant changes (server-side
  // scope follows the header selection).
  useEffect(() => {
    setSets(null)
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppId, selectedVariantId])

  /** Search across Set name and every column name — case-
   * insensitive substring match. Empty query passes everything. */
  const filtered = useMemo(() => {
    if (!sets) return []
    const q = query.trim().toLowerCase()
    let list = sets
    if (q) {
      list = sets.filter((s) => {
        if (s.name.toLowerCase().includes(q)) return true
        return s.columns.some((c) => c.name.toLowerCase().includes(q))
      })
    }
    const sorted = [...list]
    switch (sortKey) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name))
        break
      case 'rows':
        sorted.sort((a, b) => b.rowCount - a.rowCount)
        break
      case 'usedBy':
        sorted.sort((a, b) => b.usedByCount - a.usedByCount)
        break
      case 'updated':
      default:
        sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        break
    }
    return sorted
  }, [sets, query, sortKey])

  async function handleCreate() {
    const name = newName.trim()
    if (!name) return
    setCreating(true)
    try {
      // Stamp the new Set with the current app + variant so it lands in
      // this same scoped view (not as a workspace-level orphan that the
      // scoped list would hide).
      const { set } = await testDataSetsAPI.create(name, [], {
        applicationId: selectedAppId,
        variantId: selectedVariantId,
      })
      toast({ title: `Test Data Set "${name}" created`, variant: 'success' })
      setNewOpen(false)
      setNewName('')
      navigate(wp(`/test-data/${set._id}`))
    } catch (err) {
      toast({ title: 'Could not create Set', description: err instanceof Error ? err.message : String(err), variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(set: TestDataSetSummary) {
    const usedBy = set.usedByCount
    const confirmed = await confirm({
      title: `Delete "${set.name}"?`,
      description:
        usedBy > 0
          ? `This Set is used by ${usedBy} test${usedBy === 1 ? '' : 's'}. Deleting it leaves those tests without a Set — they keep their column-to-field mappings but need a new Set picked before they can run. This cannot be undone.`
          : set.rowCount > 0
            ? `This Set has ${set.rowCount} row${set.rowCount === 1 ? '' : 's'} and is not used by any test. Deleting cannot be undone.`
            : `This Set is not used by any test. Deleting cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!confirmed) return
    try {
      const result = await testDataSetsAPI.delete(set._id)
      toast({
        title:
          result.orphanedBindings > 0
            ? `Deleted "${set.name}" — ${result.orphanedBindings} test${result.orphanedBindings === 1 ? '' : 's'} now need a new Set`
            : `Deleted "${set.name}"`,
        variant: 'success',
      })
      await refresh()
    } catch (err) {
      toast({ title: 'Delete failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
    }
  }

  // Scope gate — same shape as Tests/Runs/Start. Test Data Sets belong
  // to a specific app + variant; without both chosen there's nothing to
  // scope to, so prompt for the header selection rather than dumping the
  // whole workspace.
  if (!selectedApp || !selectedAppId || !selectedVariantId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-semibold tracking-tight mb-6">Test Data</h1>
        <EmptyState
          icon={Database}
          title="Pick an app and variant"
          body="Test Data is scoped to a specific app and variant. Choose both from the header above to see this variant's data."
        />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Test Data</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {sets
              ? sets.length === 0
                ? `No Sets for ${selectedVariant?.name ?? 'this variant'} yet.`
                : `${sets.length} ${sets.length === 1 ? 'Set' : 'Sets'} for ${selectedVariant?.name ?? 'this variant'}.`
              : `${selectedApp.name} · ${selectedVariant?.name ?? 'variant'}`}
          </p>
        </div>
        <Button onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New Set
        </Button>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search by Set name or column"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-muted-foreground">Sort:</label>
            <div className="relative">
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className="text-sm border rounded px-2 py-1 pr-6 bg-background appearance-none"
              >
                <option value="updated">Last modified</option>
                <option value="name">Name</option>
                <option value="rows">Row count</option>
                <option value="usedBy">Used by</option>
              </select>
              <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <div className="border border-destructive bg-destructive/10 text-destructive rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {sets === null && !error && (
        <Card className="p-6 text-sm text-muted-foreground">Loading Test Data Sets…</Card>
      )}

      {sets !== null && sets.length === 0 && !error && (
        <EmptyState
          icon={Database}
          title={`No Test Data yet for ${selectedVariant?.name ?? 'this variant'}`}
          body="Sets are created automatically when a test in this variant is generated. You can also create one now to add rows manually or upload a CSV."
          action={{
            label: 'Create your first Set',
            onClick: () => setNewOpen(true),
          }}
        />
      )}

      {sets !== null && sets.length > 0 && filtered.length === 0 && (
        <Card className="p-6 text-sm text-muted-foreground">No Sets match "{query}".</Card>
      )}

      {filtered.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                <th className="text-left px-3 py-2 font-medium">Columns</th>
                <th className="text-right px-3 py-2 font-medium">Rows</th>
                <th className="text-right px-3 py-2 font-medium">Used by</th>
                <th className="text-left px-3 py-2 font-medium">Last modified</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <CatalogRow
                  key={s._id}
                  set={s}
                  onOpen={() => navigate(wp(`/test-data/${s._id}`))}
                  onOpenUsedBy={() => navigate(wp(`/test-data/${s._id}?tab=used-by`))}
                  onDelete={() => handleDelete(s)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={newOpen} onOpenChange={(open) => { if (!open && !creating) { setNewOpen(false); setNewName('') } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Test Data Set</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <label className="block text-sm font-medium mb-1">Set name</label>
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Customers"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate()
              }}
            />
            <p className="text-xs text-muted-foreground mt-2">
              You'll add columns and rows in the next step. The system column
              {' '}<code>$CONSUMED</code>{' '} is added automatically.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewOpen(false); setNewName('') }} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!newName.trim() || creating}>
              {creating ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function CatalogRow({
  set,
  onOpen,
  onOpenUsedBy,
  onDelete,
}: {
  set: TestDataSetSummary
  onOpen: () => void
  onOpenUsedBy: () => void
  onDelete: () => void
}) {
  // Show the first 5 user columns; system columns ($CONSUMED) and any
  // beyond five collapse into "+N more" so a wide-column Set doesn't
  // wrap the row.
  const userColumns = set.columns.filter((c) => !c.system)
  const visibleCols = userColumns.slice(0, 5)
  const overflow = userColumns.length - visibleCols.length
  const sensitiveCount = userColumns.filter((c) => c.sensitive).length

  return (
    <tr
      className="border-t cursor-pointer hover:bg-muted/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={onOpen}
      // Native <tr> isn't keyboard-operable — without tabIndex +
      // role + onKeyDown, a keyboard or screen-reader user can't
      // trigger the row drill-down. Same shape SystemFixtures uses.
      // Copilot review C5 on PR #1274.
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      tabIndex={0}
      role="link"
      aria-label={`Open Set ${set.name}`}
    >
      <td className="px-3 py-3">
        <div className="font-medium">{set.name}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Updated {formatRelative(new Date(set.updatedAt))}
        </div>
      </td>
      <td className="px-3 py-3 text-muted-foreground">
        <div className="flex items-center gap-1 flex-wrap">
          {visibleCols.map((c) => (
            <Badge key={c.name} variant="outline" className="font-normal">
              {c.sensitive && <Lock className="h-3 w-3 mr-1 text-amber-600" />}
              {c.name}
            </Badge>
          ))}
          {overflow > 0 && (
            <span className="text-xs">+{overflow} more</span>
          )}
          {sensitiveCount > 0 && (
            <span className="text-xs text-amber-700 ml-1">
              ({sensitiveCount} sensitive)
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-3 text-right tabular-nums">
        {set.rowCount.toLocaleString()}
      </td>
      <td className="px-3 py-3 text-right">
        {set.usedByCount === 0 ? (
          <span className="text-muted-foreground tabular-nums">—</span>
        ) : (
          <button
            className="inline-flex items-center gap-1 text-xs hover:underline text-foreground tabular-nums"
            onClick={(e) => {
              e.stopPropagation()
              onOpenUsedBy()
            }}
            title={`See the ${set.usedByCount} test${set.usedByCount === 1 ? '' : 's'} using this Set`}
          >
            <Link2 className="h-3 w-3 text-muted-foreground" />
            {set.usedByCount.toLocaleString()}
          </button>
        )}
      </td>
      <td className="px-3 py-3 text-muted-foreground text-xs">
        {set.lastModifiedBy ? (
          <div className="flex flex-col">
            <span className="text-foreground">{set.lastModifiedBy.name}</span>
            <span>{formatRelative(new Date(set.updatedAt))}</span>
          </div>
        ) : (
          <span>{new Date(set.updatedAt).toLocaleString()}</span>
        )}
      </td>
      <td className="px-3 py-3 w-12">
        <button
          aria-label={`Delete ${set.name}`}
          className="text-muted-foreground hover:text-destructive p-1"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  )
}

/** Crude relative-time formatter for the catalog "Updated X ago"
 * subtitle. Falls back to the full timestamp once we cross 30 days
 * because relative deltas at that scale stop being useful. */
function formatRelative(d: Date): string {
  const now = Date.now()
  const delta = now - d.getTime()
  const min = 60_000
  const hr = 60 * min
  const day = 24 * hr
  if (delta < min) return 'just now'
  if (delta < hr) return `${Math.floor(delta / min)}m ago`
  if (delta < day) return `${Math.floor(delta / hr)}h ago`
  if (delta < 30 * day) return `${Math.floor(delta / day)}d ago`
  return d.toLocaleDateString()
}
